import React, { useState } from "react";
import {
  supportTicketService,
  SupportTicket,
} from "@core/services/SupportTicketService";
import { supabase } from "@core/lib/supabase";
import { invokeSupabaseFunction } from "@core/lib/supabaseApi";
import { CheckCircle, Clock, User, AlertCircle, XCircle } from "lucide-react";

interface TicketStatusManagerProps {
  ticket: SupportTicket;
  onStatusChange: (updatedTicket: SupportTicket) => void;
  currentUserId?: string;
}

type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

export const TicketStatusManager: React.FC<TicketStatusManagerProps> = ({
  ticket,
  onStatusChange,
  currentUserId,
}) => {
  // Force recompilation - 2026-04-12
  const [isUpdating, setIsUpdating] = useState(false);

  const getStatusIcon = (status: TicketStatus) => {
    switch (status) {
      case "open":
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case "in_progress":
        return <Clock className="w-4 h-4 text-blue-500" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "closed":
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case "open":
        return "bg-yellow-900/20 text-yellow-300 border-yellow-700";
      case "in_progress":
        return "bg-blue-900/20 text-blue-300 border-blue-700";
      case "resolved":
        return "bg-green-900/20 text-green-300 border-green-700";
      case "closed":
        return "bg-gray-900/20 text-gray-300 border-gray-700";
      default:
        return "bg-gray-900/20 text-gray-300 border-gray-700";
    }
  };

  const getStatusLabel = (status: TicketStatus) => {
    switch (status) {
      case "open":
        return "Ouvert";
      case "in_progress":
        return "En cours";
      case "resolved":
        return "Résolu";
      case "closed":
        return "Fermé";
      default:
        return status;
    }
  };

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (newStatus === ticket.status || isUpdating) return;

    setIsUpdating(true);
    try {
      console.log("=== CHANGING TICKET STATUS ===");
      console.log("Ticket ID:", ticket.id);
      console.log("Old status:", ticket.status);
      console.log("New status:", newStatus);

      const updatedTicket = await supportTicketService.updateTicket(ticket.id, {
        status: newStatus,
        // Si on passe en "in_progress", assigner automatiquement
        ...(newStatus === "in_progress" && !ticket.assigned_to
          ? {
              assigned_to: currentUserId,
              assigned_at: new Date().toISOString(),
              assigned_by: currentUserId,
            }
          : {}),
      });

      console.log("✅ Status change successful:", updatedTicket);
      onStatusChange(updatedTicket);

      // Envoyer email de demande de feedback quand le ticket est résolu.
      // L'edge function exige désormais un JWT admin (ou service-role).
      if (newStatus === "resolved") {
        (async () => {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session?.access_token) {
            console.warn("send-feedback-request: pas de session active, skip");
            return;
          }
          // Fetch customer's preferred language
          const { data: profile } = await supabase
            .from("profiles")
            .select("preferred_language")
            .eq("email", ticket.email)
            .single();
          const lang = profile?.preferred_language || "fr";

          await invokeSupabaseFunction(
            "send-feedback-request",
            {
              ticketId: ticket.id,
              ticketSubject: ticket.subject,
              userEmail: ticket.email,
              lang,
            },
            { timeout: 10000 },
          );
        })().catch((err) =>
          console.error("Failed to send feedback request email:", err),
        );
      }
    } catch (error) {
      console.error("❌ Failed to update ticket status:", error);
      alert(
        "Erreur lors du changement de statut: " +
          (error instanceof Error ? error.message : String(error)),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTakeCharge = async () => {
    if (isUpdating || ticket.assigned_to) return;

    setIsUpdating(true);
    try {
      console.log("=== TAKING CHARGE OF TICKET ===");
      console.log("Ticket ID:", ticket.id);
      console.log("Current user ID:", currentUserId);

      const updatedTicket = await supportTicketService.updateTicket(ticket.id, {
        status: "in_progress" as TicketStatus,
        assigned_to: currentUserId,
        assigned_at: new Date().toISOString(),
        assigned_by: currentUserId,
      });

      console.log("✅ Update successful:", updatedTicket);

      // Notifier le parent du changement immédiatement
      onStatusChange(updatedTicket);
    } catch (error) {
      console.error("❌ Failed to take charge of ticket:", error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes("not found") ||
        errorMessage.includes("0 rows")
      ) {
        alert("Ce ticket n'existe plus. Il a probablement été supprimé.");
        // Recharger seulement en cas d'erreur critique
        window.location.reload();
      } else {
        alert("Erreur lors de la prise en charge du ticket: " + errorMessage);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const canTakeCharge =
    !ticket.assigned_to && ticket.status === "open" && currentUserId;
  const isAssigned = ticket.assigned_to && ticket.assigned_to === currentUserId;

  // Debug info (only when needed)
  const isDevelopment =
    typeof window !== "undefined" && window.location.hostname === "localhost";
  if (isDevelopment) {
    console.log("TICKET MANAGER DEBUG:", {
      ticketId: ticket.id,
      status: ticket.status,
      assignedTo: ticket.assigned_to,
      currentUserId: currentUserId,
      canTakeCharge,
      conditions: {
        notAssigned: !ticket.assigned_to,
        isOpen: ticket.status === "open",
        hasUserId: !!currentUserId,
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Statut actuel */}
      <div className="flex items-center justify-between">
        {/* <div className="flex items-center space-x-2">
          {getStatusIcon(ticket.status)}
          <span className="font-medium text-gray-300">Statut actuel:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
            {getStatusLabel(ticket.status)}
          </span>
        </div> */}

        {/* Assignation */}
        {/* {ticket.assigned_to && (
          <div className="flex items-center space-x-1 text-sm text-gray-400">
            <User className="w-4 h-4" />
            <span>Assigné à: {ticket.assigned_email || ticket.assigned_to}</span>
            {isAssigned && <span className="text-blue-400 font-medium">(Vous)</span>}
          </div>
        )} */}
      </div>

      {/* Actions rapides */}
      <div className="flex flex-wrap gap-2">
        {/* Prendre en charge */}
        {canTakeCharge && (
          <button
            onClick={handleTakeCharge}
            disabled={isUpdating}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isUpdating ? "..." : "Prendre en charge"}
          </button>
        )}

        {/* Changement de statut */}
        {isAssigned && (
          <>
            {ticket.status === "in_progress" && (
              <button
                onClick={() => handleStatusChange("resolved")}
                disabled={isUpdating}
                className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {isUpdating ? "..." : "Marquer résolu"}
              </button>
            )}

            {ticket.status === "resolved" && (
              <button
                onClick={() => handleStatusChange("closed")}
                disabled={isUpdating}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {isUpdating ? "..." : "Fermer le ticket"}
              </button>
            )}
          </>
        )}

        {/* Dropdown statut (admin) */}
        <select
          value={ticket.status}
          onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
          disabled={isUpdating}
          className="px-3 py-1.5 bg-gray-800 border border-gray-600 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="open">Ouvert</option>
          <option value="in_progress">En cours</option>
          <option value="resolved">Résolu</option>
          <option value="closed">Fermé</option>
        </select>
      </div>

      {/* Workflow info */}
      {/* <div className="text-xs text-gray-400 bg-gray-800 p-2 rounded">
        <strong className="text-gray-300">Workflow:</strong> 
        <span className="ml-1">Ouvert → En cours → Résolu → Fermé</span>
        {canTakeCharge && (
          <div className="mt-1">
            💡 Cliquez sur "Prendre en charge" pour vous assigner ce ticket
          </div>
        )}
      </div> */}
    </div>
  );
};
